import { types } from 'mobx-state-tree'

import { getRootStore } from '#state/models/getRootStore'
import { IFolder } from '#state/types'
import { extractTsRestSuccess } from '#util/extractTsRest'

import { ScriptListingBase } from './ListingBase'

export const ScriptListing = ScriptListingBase.named('ScriptListing')
  .props({
    modelType: types.frozen<'scriptListing'>('scriptListing'),
    icon: types.string,
    formatId: types.string,
    isEditingName: false,
    pendingLocationRequest: false,
  })
  .views((self) => ({
    get path() {
      return `/scripts/${self.id}`
    },
    get isSelected(): boolean {
      return getRootStore(self).location.getPathParam('scriptId') === self.id
    },
    get uuid() {
      return self.id
    },
  }))
  .actions((self) => ({
    getRootFolder(isPrivate: boolean, inTrash: boolean): IFolder | undefined {
      return getRootStore(self).getRootFolder(isPrivate, inTrash)
    },
    setName(value: string) {
      self.name = value
    },
    setIsEditingName(value: boolean) {
      self.isEditingName = value
    },
    setPendingLocationRequest(value: boolean) {
      self.pendingLocationRequest = value
    },
  }))
  .actions((self) => ({
    async destroy() {
      await self.apiClient.destroyScript(self.id)
      getRootStore(self).removeScriptFromState(self.id)
    },

    async duplicate(): Promise<void> {
      await getRootStore(self).doDebug()
      await extractTsRestSuccess(
        self.scrapi.scripts.copy({
          params: { id: self.id },
          body: { name: `Copy of ${self.name}` },
        }),
        200,
      )

      const folder = getRootStore(self).folderMap.get(self.folderId)
      const { currentOrg } = getRootStore(self)
      if (folder && currentOrg) {
        self.analytics.trackDocCreated({
          type: 'script',
          format: self.formatId,
          from: 'duplicate',
          accessLevel: folder.isPrivate ? 'private' : 'shared',
        })
      }
    },

    async moveToTrash() {
      if (!self.trashFolder) return
      await this.moveToFolder(self.trashFolder.id)
    },

    async share(folderId: string) {
      await extractTsRestSuccess(
        self.scrapi.scripts.shareScript({
          params: { id: self.id },
          body: { folderId },
        }),
        200,
      )
    },

    async moveToFolder(folderId: string) {
      await extractTsRestSuccess(
        self.scrapi.scripts.moveScript({
          params: { id: self.id },
          body: { folderId },
        }),
        200,
      )
    },

    async rename(name: string) {
      await extractTsRestSuccess(
        self.scrapi.scripts.updateScript({
          params: { id: self.id },
          body: { name },
        }),
        200,
      )
      self.setIsEditingName(false)
      self.setName(name)
      const { currentScript } = getRootStore(self)
      if (currentScript?.id === self.id) {
        currentScript.setName(name)
      }
    },
  }))
